import { useState, type FormEvent } from 'react';

const FORM_ADDR = 'https://readdy.ai/api/form/da297gbk8r5te5mqsk20';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测：被自动填充则静默当作成功
    const honeypot = (formData.get('company_alt') as string) || '';
    if (honeypot.trim()) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    const email = (formData.get('email') as string) || '';
    if (!email.trim()) {
      setStatus('error');
      setFormError('请填写邮箱地址');
      return;
    }

    setStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const res = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      if (
        res.ok &&
        parsed?.code === 'OK' &&
        !/spam/i.test(serverMsg) &&
        !/form data is spam/i.test(serverMsg)
      ) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.includes('spam')
            ? '提交内容未通过校验，请稍后再试。'
            : (serverMsg || '提交失败，请稍后再试。')
        );
      }
    } catch (err) {
      setStatus('error');
      setFormError('网络异常，请稍后再试。');
    }
  }

  return (
    <section id="contact" className="relative overflow-hidden bg-paper-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-3xl bg-secondary-800">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            {/* 左侧文案 */}
            <div className="relative p-7 md:p-12">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-300/40 bg-accent-500/20 px-4 py-1.5 text-sm text-accent-200">
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                订阅更新
              </span>
              <h2 className="mt-5 font-serif text-2xl font-bold text-paper-50 md:text-3xl">
                第一时间发现新作与合集
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-paper-100/85 md:text-base">
                留下邮箱，妖精漫画大全最新收录、热门推荐与合集更新将第一时间送达你的收件箱，
                再也不用担心错过想看的妖精漫画。
              </p>
              <div className="mt-8 flex flex-col gap-3">
                <div className="flex items-center gap-3 text-sm text-paper-100/85">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-ink-900">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  每周精选，绝无打扰
                </div>
                <div className="flex items-center gap-3 text-sm text-paper-100/85">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-ink-900">
                    <i className="ri-user-star-line" aria-hidden="true"></i>
                  </span>
                  专属合集一手掌握
                </div>
              </div>
            </div>

            {/* 右侧表单 */}
            <div className="flex items-center border-t border-paper-100/10 bg-secondary-900/60 p-7 md:p-12 lg:border-l lg:border-t-0">
              <form
                id="newsletter-form"
                data-readdy-form="newsletter-form"
                onSubmit={handleSubmit}
                className="w-full"
              >
                <label htmlFor="newsletter-email" className="block text-sm font-medium text-paper-100">
                  电子邮箱
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="mt-2 w-full rounded-lg border border-paper-100/20 bg-paper-50 px-4 py-3 text-sm text-ink-900 outline-none transition-colors focus:border-accent-400 focus:ring-2 focus:ring-accent-400/40"
                />
                {/* 蜜罐字段 */}
                <input
                  name="company_alt"
                  type="text"
                  className="yjhoneypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <label htmlFor="newsletter-name" className="mt-4 block text-sm font-medium text-paper-100">
                  你的称呼（选填）
                </label>
                <input
                  id="newsletter-name"
                  name="name"
                  type="text"
                  placeholder="怎么称呼你"
                  className="mt-2 w-full rounded-lg border border-paper-100/20 bg-paper-50 px-4 py-3 text-sm text-ink-900 outline-none transition-colors focus:border-accent-400 focus:ring-2 focus:ring-accent-400/40"
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="mt-6 w-full whitespace-nowrap rounded-lg bg-accent-500 px-6 py-3 text-sm font-semibold text-ink-900 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'loading' ? '正在提交…' : '订阅更新'}
                </button>

                {status === 'success' && (
                  <p className="mt-4 rounded-lg bg-accent-500/20 px-4 py-3 text-sm text-accent-200">
                    <i className="ri-checkbox-circle-line mr-1" aria-hidden="true"></i>
                    订阅成功，感谢你的关注，最新合集会第一时间送达。
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-4 rounded-lg bg-primary-500/20 px-4 py-3 text-sm text-primary-200">
                    <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}